import type { Alarm } from '../api/types.js';

/** The navigation badge refreshes this often. */
export const ALARM_BADGE_REFRESH_MS = 30_000;
/** The alarms page refreshes this often (the worker evaluates every 15 s). */
export const ALARMS_REFRESH_MS = 15_000;

/** Window event after an alarm changed in this page (the badge reloads). */
export const ALARMS_CHANGED_EVENT = 'kalvio:alarms-changed';

export function notifyAlarmsChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ALARMS_CHANGED_EVENT));
}

/** Text of the red counter in the navigation; empty = no badge. */
export function alarmBadgeText(open: number): string {
  if (open <= 0) return '';
  return open > 99 ? '99+' : String(open);
}

export type AlarmState = 'open' | 'acknowledged' | 'cleared';

export function alarmState(a: Pick<Alarm, 'cleared_at' | 'acked_at'>): AlarmState {
  if (a.cleared_at !== null) return 'cleared';
  return a.acked_at !== null ? 'acknowledged' : 'open';
}

/** Order of the API: open first (critical first), then newest first. */
export function compareAlarms(a: Alarm, b: Alarm): number {
  const openA = a.cleared_at === null ? 0 : 1;
  const openB = b.cleared_at === null ? 0 : 1;
  if (openA !== openB) return openA - openB;
  if (openA === 0 && a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
  if (a.raised_at !== b.raised_at) return b.raised_at - a.raised_at;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Alarm details as `key: value` texts for the table. */
export function detailLines(details: Record<string, unknown>): string[] {
  return Object.entries(details).map(([k, v]) => {
    const text =
      v === null || v === undefined
        ? '—'
        : typeof v === 'string'
          ? v
          : typeof v === 'number' || typeof v === 'boolean'
            ? String(v)
            : JSON.stringify(v);
    return `${k.replace(/_/g, ' ')}: ${text}`;
  });
}
